/**
 * Phase / segment-window UI helpers — mirrors
 * fab-planner-backend/docs/amhs_4node_rectangle_player.html
 * (phaseLabel, phaseChipClass, activeWindow, travel jump).
 */

import type { AmhsFinalSimulationLeg, AmhsSegmentWindow } from '../types/simulationPlayback';

export type SimulationPhaseChip = 'travel' | 'deadhead' | 'wait' | 'idle';

export function isTravelPhase(leg: AmhsFinalSimulationLeg | null | undefined): boolean {
  const phase = leg?.phase;
  if (!phase) return true;
  return phase === 'delivery' || phase === 'deadhead' || phase === 'return_home';
}

/** On-map / HUD label for the active leg (Travel / Deadhead / Wait / …). */
export function phaseLabel(leg: AmhsFinalSimulationLeg | null | undefined): string {
  if (!leg) return 'Idle';
  if (leg.phase === 'return_home') return 'Home';
  if (leg.phase === 'deadhead') return 'Deadhead';
  if (leg.phase === 'delivery') return 'Travel';
  if (leg.phase === 'process_wait') {
    return leg.reason === 'segmentClearance' ? 'Clearance' : 'Wait';
  }
  return leg.phase || 'Travel';
}

export function phaseChipClass(leg: AmhsFinalSimulationLeg | null | undefined): SimulationPhaseChip {
  if (!leg) return 'idle';
  if (leg.phase === 'deadhead' || leg.phase === 'return_home') return 'deadhead';
  if (leg.phase === 'delivery') return 'travel';
  if (leg.phase === 'process_wait') return 'wait';
  return 'travel';
}

const MAX_ENDPOINT_CHARS = 18;
const TRACK_NODE_ID_RE = /^(?:track-node-\d+|NODE-\d+)$/i;
/** Typed load-port ids (`EQ-30-LP-1`, `ST-1-LP-1`) — keep as-is on OHT chips. */
const TYPED_PORT_ID_RE = /-LP-\d+$/i;

function compactName(value: string): string {
  if (value.length <= MAX_ENDPOINT_CHARS) return value;
  return value.slice(0, MAX_ENDPOINT_CHARS - 1) + '…';
}

/** Station / port name for on-vehicle tips. Track-node ids are omitted. */
export function shortEndpointLabel(
  id: string | undefined | null,
  names?: Record<string, string>
): string {
  const raw = String(id || '').trim();
  if (!raw) return '';
  // Layout docks store a short name (SL-1 / LP-1) on the same key as the typed
  // id. OHT chips should match the request loadPortId, not that display alias.
  if (TYPED_PORT_ID_RE.test(raw)) return compactName(raw);
  const named = names?.[raw]?.trim();
  if (named && !TRACK_NODE_ID_RE.test(named)) return compactName(named);
  if (TRACK_NODE_ID_RE.test(raw)) return '';
  return compactName(raw);
}

function withPlace(prefix: string, place: string | null | undefined): string {
  const name = String(place || '').trim();
  return name ? `${prefix} ${name}` : prefix;
}

export type OhtTipChip = SimulationPhaseChip | 'pick' | 'drop' | 'hold';

export type OhtTip = { label: string; chip: OhtTipChip };

export type OhtTipInput = {
  /** Streamed / replay PICKUP or DROP hold running right now. */
  transferAction?: 'PICKUP' | 'DROP' | null;
  /** Whether the FOUP is on the vehicle at this instant. */
  loaded?: boolean;
  /** Pickup station name — used for Pick From / Deadhead for. */
  pickupFrom?: string | null;
  /** Drop station name — used for Drop To. */
  dropTo?: string | null;
};

/**
 * On-vehicle tip for the active hop — what the OHT is doing, not just its phase.
 *
 * Travel names the station: Pick From while fetching, Drop To while delivering,
 * Deadhead for on empty reposition. Handoffs use the same Pick From / Drop To
 * wording. Holds keep the cause (junction, clearance) and never print a node id.
 */
export function ohtTip(
  leg: AmhsFinalSimulationLeg | null | undefined,
  input: OhtTipInput = {}
): OhtTip {
  const { transferAction, loaded, pickupFrom, dropTo } = input;
  if (transferAction === 'PICKUP') {
    return { label: withPlace('Pick From', pickupFrom), chip: 'pick' };
  }
  if (transferAction === 'DROP') {
    return { label: withPlace('Drop To', dropTo), chip: 'drop' };
  }
  if (!leg) return { label: 'Idle', chip: 'idle' };

  switch (leg.reason) {
    case 'handover':
      return { label: withPlace('Drop To', dropTo), chip: 'drop' };
    case 'deadheadToPickup':
      return { label: withPlace('Deadhead for', pickupFrom), chip: 'deadhead' };
    case 'junctionDwell':
      return { label: 'Hold · Junction', chip: 'hold' };
    case 'segmentClearance':
      return { label: 'Hold · Clearance', chip: 'hold' };
    case 'nodeCapacity':
      return { label: 'Hold · Capacity', chip: 'hold' };
    case 'parked':
      return { label: 'Parked', chip: 'idle' };
    case 'clear_dock':
    case 'park':
    case 'stepAside':
      return { label: withPlace('Deadhead for', dropTo || pickupFrom), chip: 'deadhead' };
    default:
      break;
  }

  if (leg.phase === 'return_home') {
    return { label: withPlace('Deadhead for', dropTo || 'Home'), chip: 'deadhead' };
  }
  if (leg.phase === 'deadhead') {
    return {
      label: withPlace('Deadhead for', pickupFrom || dropTo),
      chip: 'deadhead',
    };
  }
  if (leg.phase === 'delivery') {
    return loaded === false
      ? { label: withPlace('Pick From', pickupFrom), chip: 'travel' }
      : { label: withPlace('Drop To', dropTo), chip: 'travel' };
  }
  if (leg.phase === 'process_wait') {
    return loaded
      ? { label: withPlace('Hold · Drop To', dropTo), chip: 'hold' }
      : { label: withPlace('Hold · Pick From', pickupFrom), chip: 'hold' };
  }
  return { label: phaseLabel(leg), chip: phaseChipClass(leg) };
}

export function ohtTipChipColors(chip: OhtTipChip): { bg: string; fg: string } {
  switch (chip) {
    case 'pick':
      return { bg: '#115e59', fg: '#ccfbf1' };
    case 'drop':
      return { bg: '#5b21b6', fg: '#ede9fe' };
    case 'hold':
      return { bg: '#9a3412', fg: '#fed7aa' };
    default:
      return phaseChipColors(chip);
  }
}

/** Short legend label for a phase chip (Travel / Deadhead / Wait / Idle). */
export function phaseChipDisplayLabel(chip: SimulationPhaseChip): string {
  switch (chip) {
    case 'travel':
      return 'Travel';
    case 'deadhead':
      return 'Deadhead';
    case 'wait':
      return 'Wait';
    default:
      return 'Idle';
  }
}

/** Chip colors matching the HTML player legend. */
export function phaseChipColors(chip: SimulationPhaseChip): { bg: string; fg: string } {
  switch (chip) {
    case 'travel':
      return { bg: '#166534', fg: '#bbf7d0' };
    case 'deadhead':
      return { bg: '#0c4a6e', fg: '#bae6fd' };
    case 'wait':
      return { bg: '#92400e', fg: '#fde68a' };
    default:
      return { bg: '#334155', fg: '#cbd5e1' };
  }
}

/**
 * Active segment window at time t (keeps previous lit across tiny clearance gaps).
 */
export function activeSegmentWindow(
  leg: AmhsFinalSimulationLeg | null | undefined,
  t: number
): AmhsSegmentWindow | null {
  const wins = leg?.segmentWindows ?? [];
  if (!wins.length) return null;
  for (const w of wins) {
    if (t >= w.startS && t <= w.endS) return w;
  }
  for (let i = 0; i < wins.length - 1; i++) {
    const a = wins[i]!;
    const b = wins[i + 1]!;
    if (t > a.endS && t < b.startS) return a;
  }
  if (t < wins[0]!.startS) return wins[0]!;
  if (t > wins[wins.length - 1]!.endS) return wins[wins.length - 1]!;
  return null;
}

/**
 * Prefer travel/deadhead over waits when multiple legs overlap at t
 * (same ranking as the HTML player `active()`).
 *
 * Legs are indexed by ohtId (WeakMap cache) so per-frame cost is O(legs for that OHT),
 * not O(all FOUP×TR legs) — critical when FOUP count grows.
 */
const apiLegsByOhtCache = new WeakMap<
  AmhsFinalSimulationLeg[],
  Map<string, AmhsFinalSimulationLeg[]>
>();

export function indexApiLegsByOht(
  legs: AmhsFinalSimulationLeg[]
): Map<string, AmhsFinalSimulationLeg[]> {
  const cached = apiLegsByOhtCache.get(legs);
  if (cached) return cached;
  const map = new Map<string, AmhsFinalSimulationLeg[]>();
  for (const leg of legs) {
    const id = leg.ohtId;
    const list = map.get(id);
    if (list) list.push(leg);
    else map.set(id, [leg]);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
  }
  apiLegsByOhtCache.set(legs, map);
  return map;
}

function rankApiLegCandidate(a: AmhsFinalSimulationLeg, b: AmhsFinalSimulationLeg): number {
  const aTravel = isTravelPhase(a) ? 0 : 1;
  const bTravel = isTravelPhase(b) ? 0 : 1;
  if (aTravel !== bTravel) return aTravel - bTravel;
  if (a.startSec !== b.startSec) return a.startSec - b.startSec;
  return b.endSec - b.startSec - (a.endSec - a.startSec);
}

/**
 * Same ranking as `findActiveApiLeg` over an already-indexed leg list, so a
 * per-frame paint loop can index once and then scan one vehicle at a time.
 */
export function pickActiveApiLeg(
  ohtLegs: AmhsFinalSimulationLeg[] | undefined,
  t: number
): AmhsFinalSimulationLeg | null {
  if (!ohtLegs || ohtLegs.length === 0) return null;

  let best: AmhsFinalSimulationLeg | null = null;
  for (const leg of ohtLegs) {
    if (leg.startSec > t) break;
    if (t > leg.endSec) continue;
    if (!best || rankApiLegCandidate(leg, best) < 0) best = leg;
  }
  return best;
}

export function findActiveApiLeg(
  legs: AmhsFinalSimulationLeg[],
  ohtId: string,
  t: number
): AmhsFinalSimulationLeg | null {
  return pickActiveApiLeg(indexApiLegsByOht(legs).get(ohtId), t);
}

/** Ordered segment ids for path paint (windows preferred, else segmentIds). */
export function legPathSegmentIds(leg: AmhsFinalSimulationLeg): string[] {
  const wins = leg.segmentWindows;
  if (wins && wins.length > 0) return wins.map((w) => w.segmentId);
  return leg.segmentIds ?? [];
}

/**
 * Earliest travel/deadhead start that has geometry — Jump ▶ target (+0.5s in UI).
 */
export function firstTravelMotionTime(legs: AmhsFinalSimulationLeg[]): number | null {
  let tMin = Infinity;
  for (const leg of legs) {
    if (!isTravelPhase(leg)) continue;
    if ((leg.segmentIds?.length ?? 0) > 0 || (leg.segmentWindows?.length ?? 0) > 0) {
      tMin = Math.min(tMin, Number(leg.startSec) || 0);
    }
  }
  return Number.isFinite(tMin) ? tMin : null;
}

/**
 * Next travel/deadhead motion strictly after `afterSec`.
 * Prefers the next `segmentWindows[].startS` (CA clearance holes) so Skip wait
 * resumes the current trip instead of jumping to a later FOUP's TR.
 */
export function nextTravelMotionTime(
  legs: AmhsFinalSimulationLeg[],
  afterSec: number
): number | null {
  let tMin = Infinity;
  for (const leg of legs) {
    if (!isTravelPhase(leg)) continue;
    const wins = leg.segmentWindows ?? [];
    if (wins.length > 0) {
      for (const w of wins) {
        const start = Number(w.startS) || 0;
        if (start > afterSec + 1e-6) tMin = Math.min(tMin, start);
      }
      continue;
    }
    if ((leg.segmentIds?.length ?? 0) === 0) continue;
    const start = Number(leg.startSec) || 0;
    if (start > afterSec + 1e-6) tMin = Math.min(tMin, start);
  }
  return Number.isFinite(tMin) ? tMin : null;
}

export type SegmentHighlightStyle = {
  segmentId: string;
  stroke: string;
  width: number;
  opacity: number;
  dash?: number[];
  active: boolean;
};

export type OhtSegmentHighlight = {
  ohtId: string;
  requestId: string;
  phase: string;
  label: string;
  chip: SimulationPhaseChip;
  pathColor: string;
  activeSegmentId: string | null;
  styles: SegmentHighlightStyle[];
  hint: string;
};

/**
 * Build path + active-window stroke styles for one OHT at time t
 * (HTML `highlightSegmentWindows` per vehicle).
 */
export function buildOhtSegmentHighlight(
  leg: AmhsFinalSimulationLeg,
  t: number,
  ohtColor: string
): OhtSegmentHighlight {
  const phase = leg.phase || 'delivery';
  const isDh = phase === 'deadhead' || phase === 'return_home';
  const pathColor = isDh ? '#7dd3fc' : ohtColor;
  // Active segmentWindow: green (not OHT orange) so occupancy reads clearly on the rail.
  const activeColor = isDh ? '#f8fafc' : '#22c55e';
  const pathSegs = legPathSegmentIds(leg);
  const win = activeSegmentWindow(leg, t);
  const label = phaseLabel(leg);
  const chip = phaseChipClass(leg);

  const styles: SegmentHighlightStyle[] = pathSegs.map((segmentId) => {
    const isActive = win?.segmentId === segmentId;
    if (isActive) {
      return {
        segmentId,
        stroke: activeColor,
        width: isDh ? 6 : 5,
        opacity: 1,
        dash: isDh ? [10, 4] : undefined,
        active: true,
      };
    }
    return {
      segmentId,
      stroke: pathColor,
      width: isDh ? 3.2 : 3,
      opacity: isDh ? 0.55 : 0.4,
      dash: isDh ? [7, 5] : undefined,
      active: false,
    };
  });

  let hint = '';
  if (win) {
    hint = `${label} · ${String(win.segmentId).slice(-12)}`;
  } else if (isDh && pathSegs.length) {
    hint = `Deadhead path (${pathSegs.length} segs)`;
  } else if (!isDh && pathSegs.length) {
    hint = 'Travel';
  }

  return {
    ohtId: leg.ohtId,
    requestId: leg.requestId,
    phase,
    label,
    chip,
    pathColor,
    activeSegmentId: win?.segmentId ?? null,
    styles,
    hint,
  };
}

/** Highlights for all OHTs that have an active travel phase at t. */
export function buildAllSegmentHighlights(
  apiLegs: AmhsFinalSimulationLeg[],
  t: number,
  ohtColors: Record<string, string>,
  filterOhtId?: string | null
): OhtSegmentHighlight[] {
  const byOht = indexApiLegsByOht(apiLegs);
  const out: OhtSegmentHighlight[] = [];
  for (const ohtId of byOht.keys()) {
    if (filterOhtId && ohtId !== filterOhtId) continue;
    const leg = findActiveApiLeg(apiLegs, ohtId, t);
    if (!leg || !isTravelPhase(leg)) continue;
    out.push(buildOhtSegmentHighlight(leg, t, ohtColors[ohtId] || '#38bdf8'));
  }
  return out;
}

/**
 * Active-window stroke only (skips full-path dim strokes).
 * Used at high FOUP counts to keep the canvas light while still showing occupancy.
 */
export function buildAllSegmentHighlightsActiveOnly(
  apiLegs: AmhsFinalSimulationLeg[],
  t: number,
  ohtColors: Record<string, string>,
  filterOhtId?: string | null
): OhtSegmentHighlight[] {
  return buildAllSegmentHighlights(apiLegs, t, ohtColors, filterOhtId).map((h) => ({
    ...h,
    styles: h.styles.filter((s) => s.active),
  }));
}

export type TravelTargetMark = {
  ohtId: string;
  nodeId: string;
  x: number;
  y: number;
  color: string;
  label: string;
  requestId: string;
  hint: string;
};

/**
 * Travel destination marks — HTML `highlightTargetNodes`.
 * Prefer port→track-node map; fall back to the end of the active path.
 */
export function buildTravelTargetMarks(params: {
  apiLegs: AmhsFinalSimulationLeg[];
  t: number;
  ohtColors: Record<string, string>;
  portTrackNodes?: Record<string, string>;
  nodesById?: Record<string, { x: number; y: number }>;
  /** Fallback: world position at the end of each OHT's active path. */
  pathEndByOht?: Record<string, { x: number; y: number; nodeId?: string }>;
  filterOhtId?: string | null;
}): TravelTargetMark[] {
  const {
    apiLegs,
    t,
    ohtColors,
    portTrackNodes = {},
    nodesById = {},
    pathEndByOht = {},
    filterOhtId,
  } = params;
  const ohtIds = [...new Set(apiLegs.map((leg) => leg.ohtId))];
  const out: TravelTargetMark[] = [];
  for (const ohtId of ohtIds) {
    if (filterOhtId && ohtId !== filterOhtId) continue;
    const leg = findActiveApiLeg(apiLegs, ohtId, t);
    if (!leg || !isTravelPhase(leg)) continue;

    let nodeId = portTrackNodes[leg.toPortId] ?? '';
    let x: number | null = null;
    let y: number | null = null;
    if (nodeId && nodesById[nodeId]) {
      x = nodesById[nodeId].x;
      y = nodesById[nodeId].y;
    } else {
      const end = pathEndByOht[ohtId];
      if (end) {
        x = end.x;
        y = end.y;
        nodeId = end.nodeId || leg.toPortId || 'target';
      }
    }
    if (x == null || y == null) continue;

    const color = ohtColors[ohtId] || '#38bdf8';
    out.push({
      ohtId,
      nodeId,
      x,
      y,
      color,
      label: `${ohtId} →`,
      requestId: leg.requestId,
      hint: `${ohtId} → target ${String(nodeId).slice(-10)} (${leg.requestId || leg.phase})`,
    });
  }
  return out;
}
